import { useMemo, useState } from 'react'
import {
  Box, Breadcrumbs, Typography, Stack, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Table, TableHead, TableRow, TableCell, TableBody, Chip,
} from '@mui/material'
import { Visibility } from '@mui/icons-material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useQuery } from '@tanstack/react-query'
import { listGoodsReceipts, getGoodsReceipt, type GoodsReceiptListItem, type GoodsQualityStatus } from '../../api/receptii'

const QUALITY_LABEL: Record<GoodsQualityStatus, string> = { Acceptat: 'Acceptat', InControl: 'În control', Respins: 'Respins' }
const QUALITY_COLOR: Record<GoodsQualityStatus, 'success' | 'warning' | 'error'> = { Acceptat: 'success', InControl: 'warning', Respins: 'error' }

function formatDateTime(iso: string) {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export default function GoodsReceiptsPage() {
  const [detailId, setDetailId] = useState<string | null>(null)
  const { data, isLoading } = useQuery({ queryKey: ['achizitii', 'receptii'], queryFn: listGoodsReceipts })
  const { data: detail } = useQuery({ queryKey: ['achizitii', 'receptii', detailId], queryFn: () => getGoodsReceipt(detailId!), enabled: !!detailId })

  // Opțiuni de filtrare per coloană (dropdown căutabil, cu valorile existente în listă — FR cerut de client).
  const orderOptions = useMemo(() => Array.from(new Set((data ?? []).map((r) => r.purchaseOrderNumber))).sort(), [data])
  const supplierOptions = useMemo(() => Array.from(new Set((data ?? []).map((r) => r.supplierName))).sort(), [data])
  const warehouseOptions = useMemo(() => Array.from(new Set((data ?? []).map((r) => r.warehouseName))).sort(), [data])

  const columns = useMemo<GridColDef<GoodsReceiptListItem>[]>(() => [
    { field: 'number', headerName: 'Nr.', width: 130, renderCell: (p) => <code>{p.row.number}</code> },
    { field: 'receivedDate', headerName: 'Data recepției', width: 130 },
    { field: 'createdAt', headerName: 'Înregistrată la', width: 150, renderCell: (p) => formatDateTime(p.row.createdAt) },
    { field: 'receivedByUserName', headerName: 'Recepționat de', width: 160 },
    { field: 'purchaseOrderNumber', headerName: 'Comandă furnizor', width: 150, type: 'singleSelect', valueOptions: orderOptions },
    { field: 'supplierName', headerName: 'Furnizor', flex: 1, minWidth: 180, type: 'singleSelect', valueOptions: supplierOptions },
    { field: 'warehouseName', headerName: 'Depozit', width: 160, type: 'singleSelect', valueOptions: warehouseOptions },
    {
      field: 'actions', headerName: '', width: 56, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => <IconButton size="small" onClick={() => setDetailId(p.row.id)}><Visibility fontSize="small" /></IconButton>,
    },
  ], [orderOptions, supplierOptions, warehouseOptions])

  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Achiziții</Typography>
        <Typography variant="inherit" color="text.primary">Recepții</Typography>
      </Breadcrumbs>
      <Typography variant="h2">Recepții</Typography>
      <Typography variant="body2" color="text.secondary">
        Recepțiile se înregistrează din ecranul „Comenzi furnizor" (acțiunea „Recepționează"). Aici se vizualizează istoricul.
      </Typography>
      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>

      <Dialog open={!!detailId} onClose={() => setDetailId(null)} maxWidth="md" fullWidth>
        <DialogTitle>Recepție {detail?.number} — comandă {detail?.purchaseOrderNumber}</DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Articol</TableCell><TableCell align="right">Cantitate</TableCell><TableCell>Lot</TableCell>
                <TableCell>Zonă / Locație</TableCell><TableCell>Calitate</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {detail?.lines.map((l) => (
                <TableRow key={l.id}>
                  <TableCell>{l.articleCode} — {l.articleDescription}</TableCell>
                  <TableCell align="right">{l.quantityReceived}</TableCell>
                  <TableCell>{l.lotNumber ?? '—'}</TableCell>
                  <TableCell>{l.zoneName ?? '—'} {l.locationCode ? `/ ${l.locationCode}` : ''}</TableCell>
                  <TableCell><Chip size="small" label={QUALITY_LABEL[l.qualityStatus]} color={QUALITY_COLOR[l.qualityStatus]} variant="outlined" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDetailId(null)}>Închide</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}
